import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';

// Magic-link admin sign-in (v4 design, token-based flow).
//
// THE FLOW
// --------
// There are THREE modes this component handles based on URL + state:
//
//   1) Form mode  -- /dashboard/login (no ?t=)
//      User enters their email. We POST /api/admin/login-link and get
//      back a `poll_token`. The email arrives in their inbox shortly
//      after.
//
//   2) Sent + polling mode -- after form submit, same tab
//      We show "Check your inbox" AND start polling
//      GET /api/admin/login-poll?t=<poll_token> every 2.5s. When the
//      user clicks the email link in another tab, that other tab
//      confirms the token and our polling endpoint flips to
//      'authenticated', sets auth cookies on the poll response, and
//      we navigate to /dashboard. THIS is the tab that gets signed in
//      -- not the email-clicked tab. UX matches Slack/Notion.
//
//   3) Confirm mode -- /dashboard/login?t=<token>
//      The link in the email opens HERE in a new tab. We POST
//      /api/admin/login-confirm and show "Sign-in confirmed --
//      return to your original tab". No auth happens in this tab.
//
// HISTORY
// -------
// Previous version used /api/admin/login-callback with HMAC-signed
// URL params (email, exp, sig) and a 302 redirect that set cookies.
// Browsers' cookie-during-redirect heuristics (Chrome bounce tracker
// protection, Safari ITP, various extensions) silently dropped those
// cookies in some scenarios. This token-based flow fixes that and
// also gives better UX by logging in the original tab.

export default function AdminLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Confirm mode is triggered by ?t=<token> on the URL -- this is
  // the email-clicked tab path. Always sticky for the lifetime of
  // the page (don't switch out of confirm even if the param goes
  // away mid-session).
  const initialConfirmToken = useRef(searchParams.get('t') || '').current;
  const inConfirmMode = !!initialConfirmToken;

  return inConfirmMode
    ? <ConfirmView token={initialConfirmToken} />
    : <LoginView navigate={navigate} initialError={searchParams.get('error') || ''} />;
}

// ─── Login view ──────────────────────────────────────────────────────────
// Modes 1 + 2: the form, and the post-submit "Check your inbox" +
// polling. Same tab transitions between them; we keep a single
// component so the spinner / focus / error state flows smoothly.

function LoginView({ navigate, initialError }) {
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [pollToken, setPollToken] = useState(null);
  const [error, setError] = useState(initialError);

  const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());

  // If already authenticated (cookie from a previous successful sign-in)
  // skip straight to dashboard. This also runs every time we mount, so
  // returning from a stale tab after sign-in works.
  useEffect(() => {
    let alive = true;
    api('/api/admin/whoami')
      .then((r) => { if (alive && r.admin) navigate('/dashboard', { replace: true }); })
      .catch(() => {});
    return () => { alive = false; };
  }, [navigate]);

  // Polling effect -- only active once we have a poll_token. Polls every
  // 2.5s. When status flips to 'authenticated', cookies are already set
  // on the response (the worker did that side-effect on our poll call)
  // so we just navigate to /dashboard. Capped at 15 min to match the
  // server's token TTL; after that the user has to request a fresh
  // link.
  useEffect(() => {
    if (!pollToken) return;
    let alive = true;
    const startedAt = Date.now();
    const maxAge = 15 * 60 * 1000;

    async function tick() {
      if (!alive) return;
      if (Date.now() - startedAt > maxAge) {
        setError('That sign-in link expired -- request a fresh one.');
        setSent(false);
        setPollToken(null);
        return;
      }
      try {
        const r = await api(`/api/admin/login-poll?t=${encodeURIComponent(pollToken)}`);
        if (!alive) return;
        if (r.status === 'authenticated') {
          // Cookies already set by the poll response. Hop to dashboard.
          navigate('/dashboard', { replace: true });
          return;
        }
        if (r.status === 'expired' || r.status === 'consumed') {
          setError(
            r.status === 'consumed'
              ? 'This sign-in completed in another tab. Reload to continue.'
              : 'That sign-in link expired -- request a fresh one.'
          );
          setSent(false);
          setPollToken(null);
          return;
        }
        // status === 'pending' -- keep waiting.
      } catch {
        // Transient network error -- just keep trying next tick.
      }
      if (alive) setTimeout(tick, 2500);
    }
    setTimeout(tick, 1500);   // small initial delay so email has a chance to arrive
    return () => { alive = false; };
  }, [pollToken, navigate]);

  async function submit(e) {
    e?.preventDefault();
    if (!valid) { setError('Use the email on your Voiply admin account.'); return; }
    setPending(true);
    setError('');
    try {
      const r = await api('/api/admin/login-link', {
        method: 'POST',
        body: { email: email.trim().toLowerCase() },
      });
      setSent(true);
      // poll_token is only returned for allowlisted emails. If we don't
      // get one back (non-allowlisted email), we still show "check your
      // inbox" but won't actually receive anything. Anti-enumeration:
      // the user can't distinguish allowed-but-not-sent from not-allowed.
      if (r.poll_token) setPollToken(r.poll_token);
    } catch (e) {
      setError(e?.message || 'Something went wrong — please try again');
    } finally {
      setPending(false);
    }
  }

  function resend() {
    setSent(false);
    setPollToken(null);
    setError('');
  }

  return (
    <main className="admin-page admin-login-page">
      <div className="admin-login">
        {!sent ? (
          <>
            <div className="admin-login-ico" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
            <h1 className="admin-login-title">Sign in to the review dashboard</h1>
            <p className="admin-login-sub">
              Enter your Voiply admin email. We&apos;ll send you a one-time sign-in link.
            </p>

            <form onSubmit={submit} className="admin-login-form">
              <input
                type="email"
                className="admin-login-input"
                placeholder="you@voiply.com"
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                autoFocus
              />
              {error && <div className="admin-login-error">{error}</div>}
              <button
                type="submit"
                className="admin-login-submit"
                disabled={!valid || pending}
              >
                {pending ? (<><span className="submit-spinner" aria-hidden="true"></span>Sending link…</>) : 'Email me a sign-in link →'}
              </button>
            </form>
          </>
        ) : (
          <>
            <div className="admin-login-ico admin-login-ico-poll" aria-hidden="true">
              <span className="poll-ring"></span>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
            <h1 className="admin-login-title">Check your inbox.</h1>
            <p className="admin-login-sub">
              We sent a sign-in link to <strong>{email}</strong>. Tap it
              from your email and <strong>this tab will sign you in
              automatically</strong> — no need to come back here yourself.
              The link is good for 15 minutes.
            </p>
            {error && <div className="admin-login-error" style={{ marginTop: 16 }}>{error}</div>}
            <button
              type="button"
              className="inline-link"
              onClick={resend}
              style={{ marginTop: 14 }}
            >
              Send to a different email
            </button>
          </>
        )}
      </div>
    </main>
  );
}

// ─── Confirm view ────────────────────────────────────────────────────────
// Mode 3: the email-clicked tab. POST the token to /api/admin/login-confirm
// so the original tab's polling sees the click and signs in. This tab
// stays signed-OUT -- it's just a confirmation surface. The user is told
// to return to their original tab.

function ConfirmView({ token }) {
  const [state, setState] = useState('confirming');  // confirming | ok | error
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    api('/api/admin/login-confirm', { method: 'POST', body: { token } })
      .then((r) => {
        if (!alive) return;
        setEmail(r.email || '');
        setState('ok');
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || 'Could not confirm this sign-in link.');
        setState('error');
      });
    return () => { alive = false; };
  }, [token]);

  return (
    <main className="admin-page admin-login-page">
      <div className="admin-login">
        {state === 'confirming' && (
          <>
            <div className="admin-login-ico admin-login-ico-poll" aria-hidden="true">
              <span className="poll-ring"></span>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
            <h1 className="admin-login-title">Confirming sign-in…</h1>
            <p className="admin-login-sub">Just a moment.</p>
          </>
        )}

        {state === 'ok' && (
          <>
            <div className="admin-login-ico" aria-hidden="true" style={{ background: '#ECFDF5', color: '#047857', border: '2px solid #6EE7B7' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <h1 className="admin-login-title">You&apos;re signed in.</h1>
            <p className="admin-login-sub">
              {email
                ? <>We confirmed <strong>{email}</strong>. </>
                : null}
              Return to the tab where you typed your email — it&apos;ll take
              you straight to the dashboard. You can close this tab.
            </p>
            <a href="/dashboard" className="inline-link" style={{ marginTop: 14, display: 'inline-block' }}>
              Or open the dashboard here →
            </a>
          </>
        )}

        {state === 'error' && (
          <>
            <div className="admin-login-ico" aria-hidden="true" style={{ background: '#FEF2F2', color: '#B91C1C', border: '2px solid #FCA5A5' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <h1 className="admin-login-title">Sign-in didn&apos;t work</h1>
            <p className="admin-login-sub">{error}</p>
            <a href="/dashboard/login" className="inline-link" style={{ marginTop: 14, display: 'inline-block' }}>
              Request a fresh link →
            </a>
          </>
        )}
      </div>
    </main>
  );
}
